import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Shield, ShieldAlert, ShieldCheck, AlertTriangle, MapPin,
  Loader2, Building2, Calendar, ChevronRight, ListChecks,
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import ClockInAttemptsPanel from '../../components/ClockInAttemptsPanel';

const fmtDt = (s) => (s ? new Date(s).toLocaleString() : '—');
const fmtSecs = (n) => {
  if (!n) return '0s';
  const m = Math.floor(n / 60);
  return m > 0 ? `${m}m` : `${n}s`;
};

/**
 * AdminAttendanceQueue — Iter-263
 * Lists bookings flagged by GPS attendance checks. Admins can drill into the
 * GPS trail to clear or reject the shift.
 */
const AdminAttendanceQueue = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('needs_review');

  const fetchRows = async (statusFilter) => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const r = await api.get(`/gps/admin/attendance-flags${params}`);
      setRows(r.data || []);
    } catch (e) {
      toast.error('Failed to load attendance flags');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(filter); }, [filter]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-attendance-queue">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
              Attendance Verification Queue
            </h1>
            <p className="text-xs text-slate-500">Bookings flagged by GPS attendance checks</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/reliability')}
            data-testid="open-reliability-btn"
          >
            <Shield className="w-4 h-4 mr-1" />
            Reliability Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        <Tabs defaultValue="queue" className="w-full">
          <TabsList className="mb-2">
            <TabsTrigger value="queue" data-testid="tab-verification-queue">
              <ShieldAlert className="w-4 h-4 mr-1" /> Verification Queue
            </TabsTrigger>
            <TabsTrigger value="attempts" data-testid="tab-clock-in-attempts">
              <ListChecks className="w-4 h-4 mr-1" /> Clock-in Attempts
            </TabsTrigger>
          </TabsList>
          <TabsContent value="queue" className="space-y-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Filter</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-2 flex-wrap">
              {[
                { v: 'needs_review', label: 'Needs Review', tone: 'bg-amber-100 text-amber-800 border-amber-200' },
                { v: 'admin_cleared', label: 'Cleared', tone: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
                { v: 'rejected', label: 'Rejected', tone: 'bg-red-100 text-red-800 border-red-200' },
                { v: '', label: 'All', tone: 'bg-slate-100 text-slate-800 border-slate-200' },
              ].map((f) => (
                <Button
                  key={f.v || 'all'}
                  variant={filter === f.v ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter(f.v)}
                  className={filter === f.v ? f.tone : ''}
                  data-testid={`filter-${f.v || 'all'}-btn`}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-green-500" />
              <div className="font-medium">No attendance issues</div>
              <div className="text-xs">All shifts in this filter are clean.</div>
            </CardContent>
          </Card>
        ) : (
          rows.map((r) => (
            <Card
              key={r.booking_id}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/admin/gps-trail/${r.booking_id}`)}
              data-testid={`attendance-row-${r.booking_id}`}
            >
              <CardContent className="p-4 flex items-center gap-4">
                {r.talent_photo ? (
                  <img src={r.talent_photo} alt={r.talent_name} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 font-bold">
                    {r.talent_name?.[0] || '?'}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900">{r.talent_name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                    <Building2 className="w-3 h-3" /> {r.company_name}
                    <Calendar className="w-3 h-3" /> {r.shift_date}
                    <MapPin className="w-3 h-3" /> {r.venue_name}
                  </div>
                  <div className="text-xs mt-1.5 flex flex-wrap gap-1.5">
                    {r.gps_disabled_during_shift && (
                      <Badge className="bg-red-100 text-red-800 border-red-200">
                        <ShieldAlert className="w-3 h-3 mr-1" /> GPS Disabled
                      </Badge>
                    )}
                    {r.geofence_exit_flagged && (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                        <AlertTriangle className="w-3 h-3 mr-1" /> Outside Footprint · {fmtSecs(r.time_outside_geofence_seconds)}
                      </Badge>
                    )}
                    <Badge variant="outline">{r.attendance_review_status}</Badge>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-300" />
              </CardContent>
            </Card>
          ))
        )}
          </TabsContent>
          <TabsContent value="attempts">
            <ClockInAttemptsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminAttendanceQueue;
